/**
 * Pipeline Tracker Service
 *
 * API client for Content Pipeline Tracker "Mission Control" dashboard.
 *
 * Date: 2026-03-11
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface PipelineTarget {
  id: string;
  name: string;
  description: string | null;
  site_id: string;
  site_name: string;
  category_paths: string[];
  priority_levels: number[];
  brand_filter: string[];
  exclude_brands: string[];
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  status: PipelineTargetStatus;
}

export interface PipelineTargetStatus {
  // Feed stage
  feedAvailable: boolean;
  feedActive: boolean;
  schedule: string | null;
  autoApprove: boolean;
  generationLimit: number | null;
  productsInFeed: number;

  // Import stage
  productsImported: number;

  // Research stage
  categoryResearched: boolean;
  researchDetails: string;

  // Semantic gaps
  semanticGapsApplied: boolean;
  pendingGaps: number;

  // Enrichment stage
  enriched: number;
  enrichedPercent: number;

  // Scoring stage
  scored: number;
  scoredPercent: number;

  // Embedding stage
  embedded: number;
  embeddedPercent: number;
  discoveryReady: boolean;

  // AEO stage
  aeoGenerated: number;
  aeoGeneratedPercent: number;
  aeoApproved: number;
  aeoPublished: number;
  aeoPublishedPercent: number;

  // Dates
  firstApproval: string | null;
  lastApproval: string | null;

  // Overall
  overallProgress: number;
  blockedReason: string | null;
}

export interface ParsedIntent {
  siteName: string | null;
  siteId: string | null;
  categoryKeywords: string[];
  categoryPaths: string[];
  priorityLevels: number[];
  generatedName: string | null;
  matchedProducts: number;
}

export interface Site {
  id: string;
  name: string;
}

export interface Category {
  category: string;
  product_count: number;
}

export interface CreateTargetRequest {
  name: string;
  description?: string;
  siteId: string;
  categoryPaths?: string[];
  priorityLevels?: number[];
  brandFilter?: string[];
  excludeBrands?: string[];
  sortOrder?: number;
}

export interface UpdateTargetRequest {
  name?: string;
  description?: string;
  categoryPaths?: string[];
  priorityLevels?: number[];
  brandFilter?: string[];
  excludeBrands?: string[];
  sortOrder?: number;
  isActive?: boolean;
}

// API Functions

/**
 * Get all targets with computed status
 */
export async function getTargets(filters?: {
  siteId?: string;
  isActive?: boolean | 'all';
}): Promise<{ success: boolean; targets: PipelineTarget[]; count: number }> {
  const params = new URLSearchParams();
  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.isActive !== undefined) {
    params.append('isActive', String(filters.isActive));
  }

  const response = await authFetch(`/api/pipeline-tracker?${params}`);
  return response.json();
}

/**
 * Get single target with computed status
 */
export async function getTarget(targetId: string): Promise<{
  success: boolean;
  target: PipelineTarget;
}> {
  const response = await authFetch(`/api/pipeline-tracker/${targetId}`);
  return response.json();
}

/**
 * Create a new target
 */
export async function createTarget(data: CreateTargetRequest): Promise<{
  success: boolean;
  message: string;
  target: PipelineTarget;
}> {
  const response = await authFetch('/api/pipeline-tracker', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Update a target
 */
export async function updateTarget(targetId: string, data: UpdateTargetRequest): Promise<{
  success: boolean;
  message: string;
  target: PipelineTarget;
}> {
  const response = await authFetch(`/api/pipeline-tracker/${targetId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Delete (soft-delete) a target
 */
export async function deleteTarget(targetId: string): Promise<{
  success: boolean;
  message: string;
}> {
  const response = await authFetch(`/api/pipeline-tracker/${targetId}`, {
    method: 'DELETE',
  });
  return response.json();
}

/**
 * Parse natural language intent to structured criteria
 */
export async function parseIntent(text: string): Promise<{
  success: boolean;
  parsed: ParsedIntent;
}> {
  const response = await authFetch('/api/pipeline-tracker/parse-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  return response.json();
}

/**
 * Get available sites for dropdown
 */
export async function getSites(): Promise<{
  success: boolean;
  sites: Site[];
}> {
  const response = await authFetch('/api/pipeline-tracker/sites');
  return response.json();
}

/**
 * Get categories for a site
 */
export async function getCategoriesForSite(siteId: string): Promise<{
  success: boolean;
  categories: Category[];
}> {
  const response = await authFetch(`/api/pipeline-tracker/sites/${siteId}/categories`);
  return response.json();
}

// Helper functions

export function getPriorityLabel(level: number): string {
  switch (level) {
    case 1: return 'SuperTop';
    case 2: return 'Top';
    case 3: return 'MegaTop';
    default: return `Priority ${level}`;
  }
}

export function formatPriorityLevels(levels: number[]): string {
  if (!levels || levels.length === 0) return 'All Priority';
  return levels.map(getPriorityLabel).join(' & ');
}

export function getStageStatus(status: PipelineTargetStatus): Array<{
  name: string;
  complete: boolean;
  value: string | number;
  icon: 'check' | 'progress' | 'pending' | 'blocked';
}> {
  const blockedReasons = ['Feed not configured', 'Feed is paused', 'Category research not complete'];

  return [
    {
      name: 'Feed',
      complete: status.feedAvailable && status.feedActive,
      value: status.productsInFeed,
      icon: status.feedAvailable ? (status.feedActive ? 'check' : 'pending') : 'blocked',
    },
    {
      name: 'Research',
      complete: status.categoryResearched,
      value: status.categoryResearched ? 'Done' : 'Missing',
      icon: status.categoryResearched ? 'check' : 'blocked',
    },
    {
      name: 'Enrich',
      complete: status.enrichedPercent >= 95,
      value: `${status.enriched}/${status.productsImported}`,
      icon: status.enrichedPercent >= 95 ? 'check' : (status.enrichedPercent > 0 ? 'progress' : 'pending'),
    },
    {
      name: 'Score',
      complete: status.scoredPercent >= 95,
      value: status.scored,
      icon: status.scoredPercent >= 95 ? 'check' : (status.scoredPercent > 0 ? 'progress' : 'pending'),
    },
    {
      name: 'Embed',
      complete: status.embeddedPercent >= 95,
      value: status.embedded,
      icon: status.embeddedPercent >= 95 ? 'check' : (status.embeddedPercent > 0 ? 'progress' : 'pending'),
    },
    {
      name: 'Discovery',
      complete: status.discoveryReady,
      value: status.discoveryReady ? 'Ready' : 'Not Ready',
      icon: status.discoveryReady ? 'check' : 'pending',
    },
    {
      name: 'AEO',
      complete: status.aeoGeneratedPercent >= 95,
      value: status.aeoGenerated,
      icon: status.aeoGeneratedPercent >= 95 ? 'check' : (status.aeoGeneratedPercent > 0 ? 'progress' : 'pending'),
    },
    {
      name: 'Published',
      complete: status.aeoPublishedPercent >= 95,
      value: `${status.aeoPublished}/${status.aeoGenerated}`,
      icon: status.aeoPublishedPercent >= 95 ? 'check' : (status.aeoPublishedPercent > 0 ? 'progress' : 'pending'),
    },
  ];
}
